<template>
  <div class="tw-flex tw-flex-row tw-min-h-screen">
    <div class="tw-flex-1 tw-p-6">
      <div class="LoginMap tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-rounded-3xl">
        <div class="tw-flex tw-items-center tw-absolute tw-top-[60px] tw-left-[60px]">
          <div class="tw-w-[60px] tw-h-[60px]">
            <icon-logo-square />
          </div>
          <h1 class="tw-leading-9 tw-ml-4">
            <span
              class="tw-text-primary"
            >i</span>Track
          </h1>
        </div>
        <div
          class="tw-flex tw-flex-col tw-p-10 tw-pb-8 tw-rounded-3xl tw-bg-white tw-max-w-[488px] tw-overflow-hidden tw-shadow"
        >
          <h1 class="tw-tracking-wider tw-mb-8">
            {{ $t('main.recovery.title_second') }}
          </h1>
          <el-form
            ref="Form"
            :model="form"
            :rules="formRules"
            label-position="top"
            autocomplete="off"
            hide-required-asterisk
            @submit.native.prevent="submitForm"
          >
            <i-form-item
              :label="$t('common.fields.new_password')"
              prop="password"
              class="tw-mb-4"
            >
              <el-input
                v-model="form.password"
                autofocus
                autocomplete="one-time-code"
                :type="form.isShowPassword? 'text' : 'password'"
              >
                <template #suffix>
                  <icon-eye-visible
                    v-if="form.isShowPassword"
                    @click="form.isShowPassword = false"
                  />
                  <icon-eye-hidden
                    v-else
                    @click="form.isShowPassword = true"
                  />
                </template>
              </el-input>
            </i-form-item>
            <i-form-item
              :label="$t('common.fields.new_password_confirm')"
              prop="passwordConfirm"
              class="tw-mb-4"
            >
              <el-input
                v-model="form.passwordConfirm"
                :type="form.isShowPasswordConfirm ? 'text' : 'password'"
                autocomplete="one-time-code"
              >
                <template #suffix>
                  <icon-eye-visible
                    v-if="form.isShowPasswordConfirm"
                    @click="form.isShowPasswordConfirm = false"
                  />
                  <icon-eye-hidden
                    v-else
                    @click="form.isShowPasswordConfirm = true"
                  />
                </template>
              </el-input>
            </i-form-item>
            <div class="tw-flex tw-flex-col tw-gap-2 tw-mb-10">
              <PasswordValidationRule
                :is-valid="minLengthValidation"
                :message="$t('common.fields.min_length')"
              />
              <PasswordValidationRule
                :is-valid="numbersAndCharsValidation"
                :message="$t('common.fields.chars_and_digits')"
              />
              <PasswordValidationRule
                :is-valid="specialCharsValidation"
                :message="$t('common.fields.special_symbols')"
              />
            </div>

            <div class="tw-w-[548px] tw-h-px tw-ml-[-40px] tw-bg-gray tw-mb-3" />

            <div class="tw-flex tw-justify-between">
              <router-link to="/login">
                <i-button
                  type="transparent"
                  size="large"
                  class="tw-px-0 tw-text-primary"
                >
                  <icon-angle-left />
                  {{ $t('common.actions.navigate_to_login') }}
                </i-button>
              </router-link>
              <i-button
                type="primary"
                size="large"
                :disabled="disabledSaveButton || !allIsValid"
                native-type="submit"
              >
                {{ $t('common.actions.save_password') }}
              </i-button>
            </div>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '../services/AuthService';
import PasswordValidationRule from '@/components/PasswordValidationRule.vue';
import _get from 'lodash/get';

export default {
  name: 'PassRecoverySecond',
  components: {
    PasswordValidationRule,
  },
  data() {
    const validatePasswordConfirm = (rule, value, callback) => {
      if (value !== this.form.password) {
        callback(new Error(this.$t('common.fields.password_are_not_same')));
      } else {
        callback();
      }
    };

    return {
      form: {
        password: '',
        passwordConfirm: '',
        isShowPassword: false,
        isShowPasswordConfirm: false,
      },
      formRules: {
        password: [
          { required: true, message: this.$t('common.fields.required') },
        ],
        passwordConfirm: [
          { required: true, message: this.$t('common.fields.required') },
          { validator: validatePasswordConfirm, trigger: 'blur' },
        ],
      },

      disabledSaveButton: false,
    };
  },
  computed: {
    minLengthValidation() {
      const minLengthRegExp = new RegExp('.{8,}');
      return minLengthRegExp.test(this.form.password);
    },
    numbersAndCharsValidation() {
      const atLeastOneNumberAndCharRegExp = new RegExp(/^(?=.*[A-Za-z])(?=.*\d).+$/);
      return atLeastOneNumberAndCharRegExp.test(this.form.password);
    },
    specialCharsValidation() {
      const replacedPassword = this.form.password.replace(/[a-zA-Z0-9]/g, '');
      return replacedPassword.length > 0;
    },
    allIsValid() {
      return this.minLengthValidation && this.numbersAndCharsValidation && this.specialCharsValidation && this.form.password === this.form.passwordConfirm;
    },
  },
  mounted() {
    AuthService.simulateRedirect(this.$route.params.uid);
  },
  methods: {
    submitForm() {
      this.disabledSaveButton = true;

      AuthService.setNewUserPassword(
        this.$route.params.uid,
        this.form.password,
        this.form.passwordConfirm,
      )
        .then((resp) => {
          this.$message.success({
            message: this.$capitalize(this.$t('main.recovery.change_success_message')),
            showClose: true,
            offset: this.$getMessageOffsetFromTop(),
          });
          this.success = resp.data.message;
          setTimeout((_) => {
            location.replace('/login');
          }, 2500);
        })
        .catch((error) => {
          const message = _get(error, 'response.data.message');
          this.$message.error({
            message: this.$capitalize(message || this.$t('core.error.internal_server_error')),
            showClose: true,
            offset: this.$getMessageOffsetFromTop(),
          });
          this.disabledSaveButton = false;
        });
    },
  },
};
</script>

<style scoped>

</style>
